import { useLazyQuery } from "@apollo/client";
import { GoogleOAuthProvider } from "@react-oauth/google";
import jwt_decode, { JwtPayload } from "jwt-decode";
import mixpanel from "mixpanel-browser";
import React, { Suspense, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import { Route, Routes, useLocation } from "react-router-dom";
// import { IntercomProvider, useIntercom } from "react-use-intercom";
import {
  // GET_PROFILE_DETAILS,
  REFRESH_TOKEN,
} from "src/graphql/queries/auth.queries";
import {
  updateJWT,
  updateStorageStatus,
} from "src/graphql/state/auth/authActions";
import {
  getAccessToken,
  getRefreshToken,
  removeAllFromStorage,
  saveToStorage,
} from "src/helper/storage";
import useOfflineDetection from "src/hooks/useOfflineDetection";
import ContactSupport from "./components/ContactSupport";
import FallbackComponent from "./components/Fallback";
import AppContextProvider from "./context";
import DashboardLayout from "./layouts/dashboard";

// Auth / public routes (already lazy)
const Register = React.lazy(() => import("./pages/register"));
const GeneralInvite = React.lazy(() => import("./pages/general-invite"));
const Login = React.lazy(() => import("./pages/login"));
const ForgotPassword = React.lazy(() => import("./pages/forgot-password"));
const Events = React.lazy(() => import("./pages/events"));
const Profile = React.lazy(() => import("./pages/profile"));
const OAuthLogin = React.lazy(() => import("./pages/oauth"));
const Clear = React.lazy(() => import("./pages/clear"));
const AcceptTeamInvite = React.lazy(() => import("./pages/accept-team-invite"));
const NotFound = React.lazy(() => import("./pages/not-found"));

// Dashboard routes
const MyPlan = React.lazy(() => import("./pages/my-plan"));
const Invitations = React.lazy(() => import("./pages/invitations"));
const EventDetails = React.lazy(() => import("./pages/event-details"));
const EventSchedule = React.lazy(() => import("./pages/event-schedule"));
const AccessControl = React.lazy(() => import("./pages/access-control"));
const Reservation = React.lazy(() => import("./pages/reservations"));
const EventVendors = React.lazy(() => import("./pages/event-vendors"));
const Gifts = React.lazy(() => import("./pages/gifts"));
const Wallet = React.lazy(() => import("./pages/wallet"));
const Website = React.lazy(() => import("./pages/website"));
const Rsvp = React.lazy(() => import("./pages/rsvp"));
const LiveHall = React.lazy(() => import("./pages/live-hall"));
const Guests = React.lazy(() => import("./pages/guests"));
const Photobook = React.lazy(() => import("./pages/photobook"));
const AsoEbi = React.lazy(() => import("./pages/aso-ebi"));
const AsoEbiGroup = React.lazy(() => import("./pages/aso-ebi-group"));
const AsoEbiPackage = React.lazy(
  () => import("./pages/aso-ebi-group/Packages/PackagePage")
);
const Chapters = React.lazy(() => import("./pages/chapters"));
const Guides = React.lazy(() => import("./pages/guides"));
const LuxePage = React.lazy(() => import("./pages/luxe"));

// Standalone / utility routes
const StripeSetup = React.lazy(() => import("./pages/stripe-setup"));
const PaymentSetup = React.lazy(() => import("./pages/payment-setup"));
const Templates = React.lazy(() => import("./pages/templates"));
const TemplateDemo = React.lazy(
  () => import("./pages/templates/template-demo")
);
const InviteTemplates = React.lazy(() => import("./pages/invite-templates"));
const InvitationTemplateDemo = React.lazy(
  () => import("./pages/invite-templates/template-demo")
);
const InviteCardMaker = React.lazy(() => import("./pages/invite-card-maker"));

if (!!process.env.REACT_APP_MIXPANEL) {
  mixpanel.init(process.env.REACT_APP_MIXPANEL, {
    debug: process.env.NODE_ENV === "development" ? true : false,
  });
  // mixpanel.track("Tracking after mixpanel.init");
}

// Intercom initialization component
// const IntercomInitializer = () => {
//   const { boot } = useIntercom();
//   const [getProfile, { data: profile }] = useLazyQuery(GET_PROFILE_DETAILS, {
//     fetchPolicy: "cache-and-network",
//   });

//   useEffect(() => {
//     // Execute the query to get profile data
//     getProfile();
//   }, [getProfile]);

//   useEffect(() => {
//     // Always boot Intercom, with or without user data
//     try {
//       if (profile?.user) {
//         boot({
//           name: profile.user.first_name,
//           email: profile.user.email,
//           userId: profile.user.uuid,
//           phone: profile.user.phone_number,
//         });
//       } else {
//         // Boot Intercom without user data (anonymous)
//         boot();
//       }
//     } catch (error) {
//       console.error("Error booting Intercom:", error);
//       // Fallback: try to boot without user data
//       try {
//         boot();
//       } catch (fallbackError) {
//         console.error("Error booting Intercom (fallback):", fallbackError);
//       }
//     }
//   }, [boot, profile]);

//   return null;
// };

function App() {
  let location = useLocation();

  // Initialize offline detection
  useOfflineDetection();

  const [refreshTokenData] = useLazyQuery(REFRESH_TOKEN, {
    variables: {
      refresh_token: getRefreshToken(),
    },
    onCompleted(data) {
      startSilentRefresh(true);
      updateJWT(data?.refresh);
      if (
        data?.refresh.refresh_token &&
        data?.refresh.access_token &&
        data?.refresh.access_expiry
      ) {
        const { refresh_token, access_token, access_expiry } = data?.refresh;
        const jwt = { refresh_token, access_token, access_expiry };
        saveToStorage("inawo", jwt);
      }
    },
    onError({ message }) {
      // Don't navigate if already on /clear or /login to prevent loops
      if (location.pathname === "/clear" || location.pathname === "/login") {
        return;
      }

      if (message === "unauthenticated") {
        toast.error(<p className="toast">Your session has expired.</p>);
        updateStorageStatus(false);
        // Use replace to prevent back navigation issues
        window.location.replace("/login");
      } else {
        // For other errors, clear storage and redirect to login instead of /clear
        // This prevents infinite loops
        updateStorageStatus(false);
        removeAllFromStorage();
        toast.error(
          <p className="toast">An error occurred. Please log in again.</p>
        );
        window.location.replace("/login");
      }
    },
  });

  const startSilentRefresh = useCallback(
    (loggedIn: boolean) => {
      let isMounted = false;
      if (!isMounted) {
        if (loggedIn) {
          //start countdown to silent refresh after login/signup
          //let getExpiry = expiryTime();
          let accessTokenStr = getAccessToken();

          if (jwt_decode<JwtPayload>(accessTokenStr)?.exp) {
            let expiry = jwt_decode<JwtPayload>(accessTokenStr)?.exp;
            if (expiry) {
              //if (expiry * 1000 > Date.now() || getExpiry) {
              if (expiry * 1000 > Date.now()) {
                let time = expiry * 1000 - Date.now();

                if (time <= 5000) {
                  refreshTokenData();
                }
              }
              setTimeout(() => {
                refreshTokenData();
              }, expiry - 5000);
            }
          }
        }
      }
      return () => {
        isMounted = false;
      };
    },
    [refreshTokenData]
  );

  //store silent refresh function in state
  //refreshAction(startSilentRefresh);

  useEffect(() => {
    // Skip refresh token logic on /clear and /login routes to prevent loops
    if (location.pathname === "/clear" || location.pathname === "/login") {
      return;
    }

    //run silent refresh after page load if jwt isn't in memory
    let isMounted = false;
    if (!isMounted) {
      let userLoggedIn = localStorage["loggedIn"];
      if (userLoggedIn) userLoggedIn = JSON.parse(userLoggedIn);

      if (userLoggedIn) {
        //start countdown to silent refresh after login/signup
        //let getExpiry = expiryTime();
        let accessTokenStr = getAccessToken();

        if (accessTokenStr && jwt_decode<JwtPayload>(accessTokenStr)?.exp) {
          let expiry = jwt_decode<JwtPayload>(accessTokenStr)?.exp;

          if (expiry) {
            //if (expiry * 1000 >= Date.now() || getExpiry) {
            if (expiry * 1000 >= Date.now()) {
              let time = expiry * 1000 - Date.now();
              if (time <= 5000) {
                refreshTokenData();
              }
            }
          }
        }
      }
    }
    return () => {
      isMounted = false;
    };
  }, [location.pathname, refreshTokenData]);

  return (
    <div className="app">
      <GoogleOAuthProvider
        clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID as string}
      >
        {/* <IntercomProvider appId={`${process.env.REACT_APP_INTERCOM_APP_ID}`}> */}
        {/* <IntercomInitializer /> */}
        <AppContextProvider>
          <>
            <Suspense fallback={<FallbackComponent />}>
              <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/clear" element={<Clear />} />
                <Route path={`/general-invite`} element={<GeneralInvite />} />
                <Route path="/register" element={<Register />} />
                <Route path="/login" element={<Login />} />
                <Route path="oauth/login" element={<OAuthLogin />} />
                <Route path="/events" element={<Events />} />
                <Route path="/accept-team" element={<AcceptTeamInvite />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* <Route path="/slots" element={<Slots />} />
              <Route path="/slots/:slug" element={<SlotsSheetContainer />} /> */}

                {/* Dashboard routes - wrapped in DashboardLayout */}
                <Route path="/" element={<DashboardLayout />}>
                  <Route path="/dashboard" element={<MyPlan />} />
                  <Route path="/my-plan" element={<MyPlan />} />
                  <Route path="/invitations" element={<Invitations />} />

                  <Route path="/event-details" element={<EventDetails />} />
                  <Route path="/event-schedule" element={<EventSchedule />} />
                  <Route path="/access-control" element={<AccessControl />} />
                  <Route path="/reservation" element={<Reservation />} />
                  <Route path="/event-vendors" element={<EventVendors />} />
                  <Route path="/wishlist" element={<Gifts />} />
                  <Route path="/wishlist/:itemId" element={<Gifts />} />
                  <Route path="/cash-gifts" element={<Wallet />} />
                  <Route path="/website" element={<Website />} />
                  <Route path="/rsvp" element={<Rsvp />} />
                  <Route path="/live-hall" element={<LiveHall />} />
                  <Route path="/guests" element={<Guests />} />
                  <Route path="/guests/broadcasts/new" element={<Guests />} />
                  <Route
                    path="/guests/broadcasts/:draftId/edit"
                    element={<Guests />}
                  />
                  <Route path="/photobook" element={<Photobook />} />
                  <Route path="/aso-ebi" element={<AsoEbi />} />
                  <Route path="/aso-ebi/:groupId" element={<AsoEbiGroup />} />
                  <Route
                    path="/aso-ebi/:groupId/packages/new"
                    element={<AsoEbiPackage />}
                  />
                  <Route
                    path="/aso-ebi/:groupId/packages/:packageId"
                    element={<AsoEbiPackage />}
                  />
                  <Route path="/chapters" element={<Chapters />} />
                  <Route path="/guides" element={<Guides />} />
                </Route>

                <Route path="/luxe" element={<LuxePage />} />
                <Route path="/stripe-setup" element={<StripeSetup />} />
                <Route path="/website/templates" element={<Templates />} />
                <Route
                  path="/website/templates/:id"
                  element={<TemplateDemo />}
                />

                <Route
                  path="/invitations/templates"
                  element={<InviteTemplates />}
                />
                <Route
                  path="/invitations/templates/card-maker"
                  element={<InviteCardMaker />}
                />
                <Route
                  path="/invitations/templates/:id"
                  element={<InvitationTemplateDemo />}
                />
                <Route path="/payment-setup" element={<PaymentSetup />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <ContactSupport />
          </>
        </AppContextProvider>
        {/* </IntercomProvider> */}
      </GoogleOAuthProvider>
    </div>
  );
}

export default App;
